import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Search, Download, FileText, Presentation, BookOpen, Link2, ArrowRight, Filter } from 'lucide-react'
import CinematicNav, { BackToTop } from '../../components/landing/CinematicNav'
import CinematicFooter from '../../components/landing/CinematicFooter'
import Reveal from '../../components/landing/Reveal'
import Breadcrumbs from '../../components/masterclass/Breadcrumbs'
import { SCHEDULE_SERIES_NAME } from '../../utils/masterclassSchedule'
import { masterclassService } from '../../services/masterclassService'
import LoadingSpinner from '../../components/LoadingSpinner'

const RESOURCE_TYPES = ['All', 'presentation', 'speaker_notes', 'reference', 'reading', 'download']

const TYPE_ICONS = {
  presentation: Presentation,
  speaker_notes: FileText,
  reference: BookOpen,
  reading: BookOpen,
  download: Link2,
}

const TYPE_LABELS = {
  presentation: 'Presentation',
  speaker_notes: 'Speaker Notes',
  reference: 'Reference',
  reading: 'Reading',
  download: 'Download',
}

export default function ResourcesPage() {
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [activeType, setActiveType] = useState('All')
  const [scrolled, setScrolled] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40)
      setShowBackToTop(window.scrollY > 800)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    document.title = `Resources | ${SCHEDULE_SERIES_NAME}`
    setLoading(true)
    const params = {}
    if (search) params.search = search
    if (activeType !== 'All') params.resource_type = activeType
    masterclassService
      .getResources(params)
      .then((d) => setResources(d.resources || []))
      .catch(() => setResources([]))
      .finally(() => setLoading(false))
  }, [search, activeType])

  return (
    <div className="cinema-page min-h-screen bg-cinema-black">
      <div className="cinema-grain" aria-hidden="true" />
      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} />

      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="cinema-glow-orb w-[600px] h-[600px] -top-40 right-0 opacity-30" />
        <div className="cinema-container relative">
          <Reveal>
            <Breadcrumbs title="Resources" />
          </Reveal>
          <div className="max-w-3xl mt-10">
            <Reveal delay={100}>
              <span className="cinema-label">Downloads & Notes</span>
              <h1 className="cinema-headline-lg mt-6 text-white">Masterclass Resources</h1>
              <p className="cinema-body mt-6 text-cinema-muted">
                Slides, speaker notes, references, and reading materials from every monthly masterclass session.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="sticky top-0 z-30 bg-cinema-black/90 backdrop-blur-md border-b border-white/5 py-4">
        <div className="cinema-container">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 lg:pb-0 no-scrollbar">
              {RESOURCE_TYPES.map((type) => (
                <button
                  key={type}
                  onClick={() => setActiveType(type)}
                  className={`whitespace-nowrap px-4 py-2 text-xs font-sans uppercase tracking-wider transition-all ${
                    activeType === type ? 'text-cinema-glow' : 'text-cinema-muted hover:text-cinema-gold'
                  }`}
                >
                  {type === 'All' ? 'All' : TYPE_LABELS[type] || type}
                </button>
              ))}
            </div>
            <div className="flex-1" />
            <div className="relative w-full lg:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted" />
              <input
                type="text"
                placeholder="Search resources..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-cinema-graphite border-b border-white/10 text-white pl-10 pr-4 py-2.5 text-sm font-sans focus:outline-none focus:border-cinema-gold/40 transition-colors"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cinema-black py-24 sm:py-32">
        <div className="cinema-container">
          {loading ? (
            <div className="flex justify-center py-24">
              <LoadingSpinner />
            </div>
          ) : resources.length === 0 ? (
            <div className="text-center py-24">
              <div className="text-6xl mb-6">📚</div>
              <h3 className="font-display text-2xl text-white">No resources found</h3>
              <p className="text-cinema-muted font-sans mt-3">
                Resources will be published after each masterclass.
              </p>
            </div>
          ) : (
            <div className="space-y-0 divide-y divide-white/5">
              {resources.map((r, i) => {
                const Icon = TYPE_ICONS[r.resource_type] || FileText
                return (
                  <Reveal key={r.id} delay={i * 60}>
                    <div className="py-8 group">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                        <div className="w-12 h-12 bg-cinema-glow/10 flex items-center justify-center shrink-0">
                          <Icon className="w-5 h-5 text-cinema-gold" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <span className="text-cinema-gold text-xs font-sans uppercase tracking-wider">
                              {TYPE_LABELS[r.resource_type] || r.resource_type}
                            </span>
                            {r.file_size && (
                              <span className="text-cinema-muted text-xs font-sans">{r.file_size}</span>
                            )}
                          </div>
                          <h3 className="font-display text-xl text-white font-medium group-hover:text-cinema-gold transition-colors">
                            {r.title}
                          </h3>
                          {r.description && (
                            <p className="text-cinema-muted font-sans text-sm mt-2 max-w-2xl">
                              {r.description}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-4 mt-4 text-cinema-muted font-sans text-xs">
                            {r.masterclass_topic && (
                              <Link
                                to={`/masterclass/${r.masterclass_slug || r.masterclass_id}`}
                                className="hover:text-cinema-gold transition-colors"
                              >
                                {r.masterclass_topic}
                              </Link>
                            )}
                            {r.speaker_name && <span>{r.speaker_name}</span>}
                          </div>
                        </div>
                        <a
                          href={r.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2.5 text-cinema-gold text-sm font-sans hover:text-white transition-colors shrink-0"
                        >
                          <Download className="w-4 h-4" /> Download
                        </a>
                      </div>
                    </div>
                  </Reveal>
                )
              })}
            </div>
          )}
        </div>
      </section>

      <CinematicFooter contactEmail="synergymultimediaacademy@gmail.com" />
      <BackToTop visible={showBackToTop} />
    </div>
  )
}
